/**
 * Svoj Na Svom - API Client
 * Koristi PHP backend (XAMPP lokalno, GoDaddy produkcija)
 */

// API Base URL - automatski detektuj produkciju vs lokalni razvoj
const isProduction = window.location.hostname !== '127.0.0.1' && window.location.hostname !== 'localhost';

// Za produkciju koristi isti domen sa /api putanjom, za lokalno koristi XAMPP
const API_BASE_URL = isProduction 
  ? `${window.location.origin}/api`  // Produkcija: svojnasvom.com/api
  : 'http://127.0.0.1/svet-nekretnina/php-version/api';  // Lokalni XAMPP

// Storage keys
const AUTH_TOKEN_KEY = 'svojnasvom_auth_token';
const USER_DATA_KEY = 'svojnasvom_user';
const SESSION_EXPIRY_KEY = 'svojnasvom_session_expiry';
const REMEMBER_ME_KEY = 'svojnasvom_remember_me';

// Session duration (24 hours in milliseconds)
const SESSION_DURATION = 24 * 60 * 60 * 1000;

// ============================================================
// SESSION MANAGEMENT
// ============================================================

export const sessionUtils = {
  // Save session data
  saveSession: (token: string, user: any, expiresAt: number) => {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(user));
    localStorage.setItem(SESSION_EXPIRY_KEY, String(expiresAt));
  },

  // Get auth token
  getToken: (): string | null => {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  },

  // Get saved user data
  getUser: (): any | null => {
    const data = localStorage.getItem(USER_DATA_KEY);
    if (data) {
      try {
        return JSON.parse(data);
      } catch {
        return null;
      }
    }
    return null;
  },

  // Update user data
  updateUser: (updates: any) => {
    const current = sessionUtils.getUser();
    if (current) {
      localStorage.setItem(USER_DATA_KEY, JSON.stringify({ ...current, ...updates }));
    }
  },

  // Check if session is valid (not expired)
  isSessionValid: (): boolean => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const expiry = localStorage.getItem(SESSION_EXPIRY_KEY);
    
    if (!token || !expiry) return false;
    
    return Date.now() < parseInt(expiry);
  },

  // Clear session
  clearSession: () => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(USER_DATA_KEY);
    localStorage.removeItem(SESSION_EXPIRY_KEY);
  },

  // Refresh session expiry (extend by 24h)
  refreshSession: () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      localStorage.setItem(SESSION_EXPIRY_KEY, String(Date.now() + SESSION_DURATION));
    }
  },

  // Remember me functionality
  setRememberMe: (email: string, remember: boolean) => {
    if (remember) {
      localStorage.setItem(REMEMBER_ME_KEY, email);
    } else {
      localStorage.removeItem(REMEMBER_ME_KEY);
    }
  },

  getRememberedEmail: (): string | null => {
    return localStorage.getItem(REMEMBER_ME_KEY);
  },

  // Legacy compatibility
  saveUserData: (user: any) => {
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(user));
  },

  getUserData: (): any | null => {
    return sessionUtils.getUser();
  },
};

// Export as rememberMeUtils for backward compatibility
export const rememberMeUtils = sessionUtils;

// ============================================================
// API FETCH WRAPPER
// ============================================================

interface ApiResponse<T = any> {
  success?: boolean;
  data?: T;
  error?: string;
  message?: string;
  needsVerification?: boolean;
  token?: string;
  expiresAt?: number;
  user?: any;
  tokensGranted?: number;
  unreadCount?: number;
}

async function apiFetch<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = sessionUtils.getToken();
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      ...options,
      headers,
      credentials: 'include', // Include cookies for session
    });

    const data = await response.json();

    if (!response.ok) {
      // Check for expired session
      if (response.status === 401 && data.expired) {
        sessionUtils.clearSession();
        window.location.href = '/#/prijava';
      }
      
      return {
        success: false,
        error: data.error || 'Greška pri komunikaciji sa serverom',
        needsVerification: data.needsVerification,
      };
    }

    return { success: true, ...data };
  } catch (error: any) {
    console.error('API Error:', error);
    return {
      success: false,
      error: 'Greška pri povezivanju sa serverom. Pokušajte ponovo kasnije.',
    };
  }
}

// ============================================================
// AUTH FUNCTIONS
// ============================================================

export const loginUser = async (
  email: string,
  password: string,
  rememberMe: boolean = false
): Promise<{
  success: boolean;
  user?: any;
  error?: string;
  needsVerification?: boolean;
}> => {
  const result = await apiFetch('auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password, rememberMe }),
  });

  if (result.success && result.user) {
    // Save session
    sessionUtils.saveSession(
      result.token || '',
      result.user,
      result.expiresAt || Date.now() + SESSION_DURATION
    );
    
    // Remember email if requested
    sessionUtils.setRememberMe(email, rememberMe);

    return { success: true, user: result.user };
  }

  return {
    success: false,
    error: result.error,
    needsVerification: result.needsVerification,
  };
};

/**
 * Get reCAPTCHA configuration from server
 */
export const getRecaptchaConfig = async (): Promise<{
  enabled: boolean;
  siteKey: string | null;
}> => {
  try {
    const result = await apiFetch('config/recaptcha');
    return {
      enabled: result.enabled || false,
      siteKey: result.siteKey || null,
    };
  } catch {
    return { enabled: false, siteKey: null };
  }
};

export const registerUser = async (
  email: string,
  password: string,
  name: string,
  userType: 'fizicko_lice' | 'pravno_lice' = 'fizicko_lice',
  recaptchaToken?: string
): Promise<{
  success: boolean;
  user?: any;
  error?: string;
  needsVerification?: boolean;
}> => {
  const result = await apiFetch('auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, name, userType, recaptcha_token: recaptchaToken }),
  });

  return {
    success: result.success || false,
    error: result.error,
    needsVerification: result.needsVerification || true,
  };
};

export const logoutUser = async (): Promise<void> => {
  await apiFetch('auth/logout', { method: 'POST' });
  sessionUtils.clearSession();
};

export const getCurrentUser = async (): Promise<any | null> => {
  if (!sessionUtils.isSessionValid()) {
    sessionUtils.clearSession();
    return null;
  }

  const result = await apiFetch('me');
  
  if (result.success && result.data) {
    // Update local cache
    sessionUtils.updateUser(result.data);
    return result.data;
  }

  return null;
};

/**
 * Refresh user data from API (tokens, notifications count, etc.)
 * Call this on app mount and after any action that might change user data
 */
export const refreshUserData = async (): Promise<{
  success: boolean;
  user?: any;
  tokenBalance?: number;
  unreadNotifications?: number;
  error?: string;
}> => {
  if (!sessionUtils.isSessionValid()) {
    return { success: false, error: 'Session invalid' };
  }

  try {
    const result = await apiFetch('me');
    
    if (result.success && result.data) {
      // Update local cache
      sessionUtils.updateUser(result.data);
      
      return {
        success: true,
        user: result.data,
        tokenBalance: result.data.tokenBalance || 0,
        unreadNotifications: result.unreadNotifications || 0,
      };
    }
    
    return { success: false, error: result.error || 'Failed to fetch user data' };
  } catch (error) {
    console.error('Error refreshing user data:', error);
    return { success: false, error: 'Network error' };
  }
};

export const resendVerificationEmail = async (): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> => {
  const result = await apiFetch('auth/resend-verification', {
    method: 'POST',
  });

  return {
    success: result.success || false,
    message: result.message,
    error: result.error,
  };
};

export const verifyEmail = async (token: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> => {
  const result = await apiFetch('auth/verify-email', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });

  return {
    success: result.success || false,
    message: result.message,
    error: result.error,
  };
};

export const requestPasswordReset = async (email: string): Promise<{ 
  success: boolean; 
  error?: string 
}> => {
  const result = await apiFetch('auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });

  return {
    success: result.success || false,
    error: result.error,
  };
};

// ============================================================
// PROFILE FUNCTIONS
// ============================================================

export const updateProfile = async (data: {
  name?: string;
  phone?: string;
}): Promise<{ success: boolean; error?: string }> => {
  const result = await apiFetch('profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  });

  if (result.success) {
    sessionUtils.updateUser(data);
  }

  return {
    success: result.success || false,
    error: result.error,
  };
};

// ============================================================
// TOKEN FUNCTIONS
// ============================================================

export const getTokenBalance = async (): Promise<number> => {
  const result = await apiFetch('tokens');
  return result.data?.balance || 0;
};

export const purchaseTokens = async (
  amount: number,
  packageName: string
): Promise<{ success: boolean; error?: string }> => {
  const result = await apiFetch('tokens/purchase', {
    method: 'POST',
    body: JSON.stringify({ amount, packageName }),
  });

  return {
    success: result.success || false,
    error: result.error,
  };
};

export const redeemPromoCode = async (code: string): Promise<{
  success: boolean;
  message?: string;
  tokensGranted?: number;
  error?: string;
}> => {
  const result = await apiFetch('tokens/redeem', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });

  return {
    success: result.success || false,
    message: result.message,
    tokensGranted: result.tokensGranted,
    error: result.error,
  };
};

// ============================================================
// PROPERTY FUNCTIONS
// ============================================================

// Transform API response (snake_case) to frontend format (camelCase) for Potražnja fields
const transformPropertyFromApi = (property: any): any => {
  if (!property) return null;
  return {
    ...property,
    // Potražnja-specific fields (snake_case -> camelCase)
    priceMin: property.price_min ? parseFloat(property.price_min) : undefined,
    priceMax: property.price_max ? parseFloat(property.price_max) : undefined,
    squareMetersMin: property.square_meters_min ? parseFloat(property.square_meters_min) : undefined,
    squareMetersMax: property.square_meters_max ? parseFloat(property.square_meters_max) : undefined,
    roomsMin: property.rooms_min || undefined,
    roomsMax: property.rooms_max || undefined,
    plotAreaMin: property.plot_area_min ? parseFloat(property.plot_area_min) : undefined,
    plotAreaMax: property.plot_area_max ? parseFloat(property.plot_area_max) : undefined,
    heatingTypes: property.heating_types ? (typeof property.heating_types === 'string' ? JSON.parse(property.heating_types) : property.heating_types) : undefined,
    nearbyLocation: property.nearby_location || undefined,
    // Standard fields that need type conversion
    price: property.price ? parseFloat(property.price) : 0,
    squareMeters: property.square_meters ? parseFloat(property.square_meters) : 0,
    plotArea: property.plot_area ? parseFloat(property.plot_area) : undefined,
    adKind: property.ad_kind,
    adType: property.ad_type,
    // Urgent/HITNO field
    isUrgent: property.is_urgent === 1 || property.is_urgent === '1' || property.is_urgent === true,
    // Other common fields
    contactPhone: property.contact_phone,
    contactEmail: property.contact_email,
    totalFloors: property.total_floors,
    heatingType: property.heating_type,
    isAuction: property.is_auction,
    auctionEndDate: property.auction_end_date,
    startingBid: property.starting_bid ? parseFloat(property.starting_bid) : undefined,
    currentBid: property.current_bid ? parseFloat(property.current_bid) : undefined,
    durationDays: property.duration_days,
    tokensSpent: property.tokens_spent,
    expiresAt: property.expires_at,
    createdAt: property.created_at,
    updatedAt: property.updated_at,
    userId: property.user_id,
    hasPendingEdit: property.has_pending_edit,
  };
};

export const getPublicStats = async (): Promise<{ users: number; activeListings: number }> => {
  const result = await apiFetch('stats');
  return result.data || { users: 0, activeListings: 0 };
};

export const getProperties = async (params?: {
  type?: 'offer' | 'demand' | 'auction' | 'all';
  limit?: number;
  offset?: number;
}): Promise<any[]> => {
  const query = new URLSearchParams();
  if (params?.type) query.set('type', params.type);
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.offset) query.set('offset', String(params.offset));

  const result = await apiFetch(`properties?${query.toString()}`);
  const properties = result.data || [];
  return properties.map(transformPropertyFromApi);
};

export const getMyListings = async (): Promise<any[]> => {
  const result = await apiFetch('my-listings');
  const listings = result.data || [];
  return listings.map(transformPropertyFromApi);
};

export const getProperty = async (id: string | number): Promise<any | null> => {
  const result = await apiFetch(`properties/${id}`);
  return transformPropertyFromApi(result.data);
};

export const createProperty = async (data: any): Promise<{
  success: boolean;
  propertyId?: number;
  error?: string;
}> => {
  const result = await apiFetch('properties', {
    method: 'POST',
    body: JSON.stringify(data),
  });

  return {
    success: result.success || false,
    propertyId: result.propertyId,
    error: result.error,
  };
};

export const updatePropertyApi = async (
  id: string | number,
  data: any
): Promise<{ success: boolean; error?: string }> => {
  const result = await apiFetch(`properties/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

  return {
    success: result.success || false,
    error: result.error,
  };
};

export const deletePropertyApi = async (
  id: string | number
): Promise<{ success: boolean; error?: string }> => {
  const result = await apiFetch(`properties/${id}`, {
    method: 'DELETE',
  });

  return {
    success: result.success || false,
    error: result.error,
  };
};

// ============================================================
// NOTIFICATION FUNCTIONS
// ============================================================

export const getNotifications = async (): Promise<{
  notifications: any[];
  unreadCount: number;
}> => {
  const result = await apiFetch('notifications');
  return {
    notifications: result.data || [],
    unreadCount: result.unreadCount || 0,
  };
};

export const markNotificationRead = async (id: string | number): Promise<void> => {
  await apiFetch(`notifications/${id}/read`, { method: 'PUT' });
};

export const markAllNotificationsRead = async (): Promise<void> => {
  await apiFetch('notifications/read-all', { method: 'PUT' });
};

// ============================================================
// FAVORITES API
// ============================================================

export const getFavorites = async (): Promise<{
  success: boolean;
  favorites: any[];
  error?: string;
}> => {
  const result = await apiFetch('favorites');
  return {
    success: result.success || false,
    favorites: result.favorites || [],
    error: result.error,
  };
};

export const toggleFavorite = async (propertyId: string): Promise<{
  success: boolean;
  isFavorite: boolean;
  message?: string;
  error?: string;
}> => {
  const result = await apiFetch(`favorites/toggle/${propertyId}`, {
    method: 'POST',
  });
  return {
    success: result.success || false,
    isFavorite: result.isFavorite || false,
    message: result.message,
    error: result.error,
  };
};

export const checkFavorite = async (propertyId: string): Promise<boolean> => {
  const result = await apiFetch(`favorites/check/${propertyId}`);
  return result.isFavorite || false;
};

// ============================================================
// AI CHAT FUNCTION
// ============================================================

export const sendAIMessage = async (message: string): Promise<{
  success: boolean;
  response?: string;
  error?: string;
}> => {
  const result = await apiFetch('ai/chat', {
    method: 'POST',
    body: JSON.stringify({ message }),
  });

  return {
    success: result.success || false,
    response: result.response,
    error: result.error,
  };
};

// ============================================================
// LEGACY COMPATIBILITY - For existing code that uses client.api.fetch
// ============================================================

export const client = {
  api: {
    fetch: async (endpoint: string, options?: RequestInit) => {
      // Remove leading /api/ if present
      const cleanEndpoint = endpoint.replace(/^\/api\//, '');
      
      // Don't set Content-Type for FormData (browser sets it with boundary)
      const isFormData = options?.body instanceof FormData;
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${sessionUtils.getToken() || ''}`,
      };
      if (!isFormData) {
        headers['Content-Type'] = 'application/json';
      }
      
      const response = await fetch(`${API_BASE_URL}/${cleanEndpoint}`, {
        ...options,
        headers: {
          ...headers,
          ...(options?.headers || {}),
        },
        credentials: 'include',
      });

      return response;
    },
  },
  auth: {
    // Compatibility methods
    getSession: async () => {
      if (!sessionUtils.isSessionValid()) {
        return { data: { session: null, user: null } };
      }
      const user = sessionUtils.getUser();
      return {
        data: {
          session: user ? { token: sessionUtils.getToken() } : null,
          user,
        },
      };
    },
    signOut: async () => {
      await logoutUser();
      return { error: null };
    },
  },
};

// ============================================================
// BIDS (AUCTIONS) API
// ============================================================

export const placeBidApi = async (
  propertyId: string,
  amount: number,
  comment?: string
): Promise<{
  success: boolean;
  data?: any;
  message?: string;
  error?: string;
}> => {
  const response = await client.api.fetch(`/properties/${propertyId}/bids`, {
    method: 'POST',
    body: JSON.stringify({ amount, comment }),
  });
  return response.json();
};

export const getBidsApi = async (propertyId: string): Promise<{
  success: boolean;
  data?: any[];
  error?: string;
}> => {
  const response = await client.api.fetch(`/properties/${propertyId}/bids`);
  return response.json();
};

// ============================================================
// NEWSLETTER API
// ============================================================

export const subscribeToNewsletter = async (email: string, name?: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> => {
  const response = await fetch(`${API_BASE_URL}/newsletter.php?action=subscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, name }),
  });
  
  const data = await response.json();
  return data;
};

export const unsubscribeFromNewsletter = async (email: string, token?: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> => {
  const params = new URLSearchParams({ email });
  if (token) params.append('token', token);
  
  const response = await fetch(`${API_BASE_URL}/newsletter.php?action=unsubscribe&${params.toString()}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  const data = await response.json();
  return data;
};
